import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../../../backend/convex/_generated/api';
import type { Doc } from '../../../../../backend/convex/_generated/dataModel';
import { WorkspaceAdminLayout } from '../../components/WorkspaceAdminLayout';
import { useWorkspace } from '../../hooks/useWorkspace';
import { AlertModal } from '../../components/Modal';
import { ConfirmModal } from '../../components/ConfirmModal';
import { useAlert, useConfirm } from '../../hooks/useAdminUI';

import { ProblemForm } from './ProblemForm';

export const ProblemManagement: React.FC = () => {
  const { workspace } = useWorkspace();
  const problems = useQuery(api.problems.list, 
    workspace ? { workspaceId: workspace._id } : "skip"
  );
  
  useEffect(() => {
    document.title = 'LibreRank - Problems';
  }, []);

  const createProblem = useMutation(api.problems.create);
  const updateProblem = useMutation(api.problems.update);
  const removeProblem = useMutation(api.problems.remove);
  const seedSampleProblems = useMutation(api.problems.seedSampleProblems);

  // Form state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingProblemId, setEditingProblemId] = useState<any>(null);
  const fullProblemToEdit = useQuery(api.problems.getForModerator, 
    editingProblemId ? { docId: editingProblemId } : "skip"
  );

  // Modal state
  const { alert, showAlert, closeAlert } = useAlert();
  const { confirm, showConfirm, closeConfirm } = useConfirm<{ docId: any; title: string }>();

  const handleCreateClick = () => {
    setEditingProblemId(null);
    setIsFormOpen(true);
  };

  const handleEditClick = (docId: any) => {
    setEditingProblemId(docId);
    setIsFormOpen(true);
  };

  const handleFormSubmit = async (data: any) => {
    if (!workspace) return;

    try {
      if (editingProblemId) {
        await updateProblem({
          docId: editingProblemId,
          ...data
        });
        showAlert('Problem Updated', `Successfully updated "${data.title}"`, 'success');
      } else {
        await createProblem({
          workspaceId: workspace._id,
          ...data
        });
        showAlert('Problem Created', `Successfully created "${data.title}"`, 'success');
      }
      setIsFormOpen(false);
      setEditingProblemId(null);
    } catch (err) {
      showAlert(
        editingProblemId ? 'Update Failed' : 'Creation Failed',
        err instanceof Error ? err.message : 'An unknown error occurred',
        'error'
      );
    }
  };

  const handleDeleteClick = (docId: any, title: string) => {
    showConfirm('Delete Problem', { docId, title }, 'delete');
  };

  const handleSeedClick = () => {
    showConfirm('Sample Problems', { docId: null, title: 'Sample Problems' }, 'seed');
  };

  const handleConfirmAction = async () => {
    if (!workspace) return;

    if (confirm.type === 'delete' && confirm.data?.docId) {
      try {
        await removeProblem({ docId: confirm.data.docId });
      } catch (err) {
        showAlert('Delete Failed', err instanceof Error ? err.message : 'An unknown error occurred', 'error');
      }
    } else if (confirm.type === 'seed') {
      try {
        await seedSampleProblems({ workspaceId: workspace._id });
        showAlert('Success', 'Sample problems have been seeded into this workspace.', 'success');
      } catch (err) {
        showAlert('Seed Failed', err instanceof Error ? err.message : 'An unknown error occurred', 'error');
      }
    }
    
    closeConfirm();
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'var(--success)';
      case 'medium': return 'var(--warning)';
      case 'hard': return 'var(--destructive)';
      default: return 'var(--foreground)';
    }
  };

  if (isFormOpen) {
    return (
      <WorkspaceAdminLayout title={editingProblemId ? "Edit Problem" : "Create Problem"}>
        <div className="max-w-4xl mx-auto">
          <ProblemForm 
            initialData={editingProblemId ? fullProblemToEdit : undefined}
            onSubmit={handleFormSubmit}
            onCancel={() => {
              setIsFormOpen(false);
              setEditingProblemId(null);
            }}
            isSubmitting={false}
          />
        </div>
      </WorkspaceAdminLayout>
    );
  }

  return (
    <WorkspaceAdminLayout title="Problem Management">
      <AlertModal 
        isOpen={alert.isOpen} 
        onClose={closeAlert} 
        title={alert.title} 
        message={alert.message} 
        type={alert.type} 
      />
      
      <ConfirmModal 
        isOpen={confirm.isOpen}
        title={confirm.type === 'delete' ? "Delete Problem" : "Seed Sample Problems"}
        message={confirm.type === 'delete' 
          ? `Are you sure you want to delete "${confirm.data?.title}"? This will remove it from all assessments.`
          : "Are you sure you want to seed the standard sample problems into this workspace? This will add common problems like Two Sum and Fibonacci."
        }
        confirmText={confirm.type === 'delete' ? "Delete" : "Seed Problems"}
        confirmVariant={confirm.type === 'delete' ? "destructive" : "success"}
        onConfirm={handleConfirmAction}
        onCancel={closeConfirm}
      />

      <div className="flex justify-between items-center mb-8">
        <div>
          <p className="text-muted-foreground">
            Manage problems available in this workspace. You can create your own or seed from samples.
          </p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={handleSeedClick}
            className="px-4 py-2 bg-secondary text-secondary-foreground border border-border rounded-md hover:bg-secondary/80 transition-colors font-semibold"
          >
            Seed Samples
          </button>
          <button 
            onClick={handleCreateClick}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors font-semibold"
          >
            Create Problem
          </button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">
        {!problems ? (
          <div className="p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
            <p className="text-muted-foreground">Loading problems...</p>
          </div>
        ) : problems.length === 0 ? (
          <div className="p-16 text-center">
            <div className="bg-muted w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">📝</span>
            </div>
            <h3 className="text-lg font-medium mb-1">No problems found</h3>
            <p className="text-muted-foreground mb-6">
              Get started by creating your first problem or seeding from our samples.
            </p>
            <button 
              onClick={handleCreateClick}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors font-semibold"
            >
              Create Your First Problem
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left table-fixed">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="p-4 font-semibold text-sm w-[45%]">Problem</th>
                  <th className="p-4 font-semibold text-sm text-center w-[15%]">Difficulty</th>
                  <th className="p-4 font-semibold text-sm w-[20%]">Slug</th>
                  <th className="p-4 font-semibold text-sm text-right w-[20%]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {problems.map((problem: Doc<"problems">) => (
                  <tr key={problem._id} className="hover:bg-muted/30 transition-colors">
                    <td className="p-4">
                      <div className="font-medium text-foreground truncate" title={problem.title}>{problem.title}</div>
                      <div className="text-xs text-muted-foreground truncate" title={problem.description}>
                        {problem.description}
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      <span 
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold capitalize"
                        style={{ 
                          backgroundColor: `${getDifficultyColor(problem.difficulty)}20`,
                          color: getDifficultyColor(problem.difficulty)
                        }}
                      >
                        {problem.difficulty}
                      </span>
                    </td>
                    <td className="p-4">
                      <code className="text-[10px] bg-muted px-1.5 py-0.5 rounded text-muted-foreground break-all">
                        {problem.id}
                      </code>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex gap-2 justify-end">
                        <button 
                          onClick={() => handleEditClick(problem._id)}
                          className="px-3 py-1 text-sm bg-transparent border border-border rounded hover:bg-accent transition-colors whitespace-nowrap"
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => handleDeleteClick(problem._id, problem.title)}
                          className="px-3 py-1 text-sm text-destructive border border-destructive/30 rounded hover:bg-destructive/10 transition-colors whitespace-nowrap"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </WorkspaceAdminLayout>
  );
};

